import { useState, useCallback, useEffect } from 'react';
import Swal from 'sweetalert2';
import * as paymentService from '../services/paymentService';
import { downloadFile } from '../utils/paymentUtils';

// Helper function to filter payments
const filterPayments = (payments, filters) => {
  const { statusFilter, searchTerm, formTypeFilter, amountRange, sortConfig } = filters;
  
  let result = [...payments];
  
  // Apply status filter
  if (statusFilter !== 'all') {
    result = result.filter(payment => payment.status === statusFilter);
  }
  
  // Apply search filter
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    result = result.filter(payment => 
      (payment.name && payment.name.toLowerCase().includes(searchLower)) ||
      (payment.email && payment.email.toLowerCase().includes(searchLower)) ||
      (payment.transactionId && payment.transactionId.toLowerCase().includes(searchLower)) ||
      (payment._id && payment._id.toLowerCase().includes(searchLower))
    );
  }
  
  // Apply form type filter
  if (formTypeFilter !== 'all') {
    result = result.filter(payment => payment.formType === formTypeFilter);
  }
  
  // Apply amount range filter
  if (amountRange !== 'all') {
    const [min, max] = amountRange.split('-').map(v => parseInt(v, 10));
    
    if (amountRange === '2001+') {
      result = result.filter(payment => payment.amount >= 2001);
    } else {
      result = result.filter(payment => payment.amount >= min && payment.amount <= (max || Number.MAX_SAFE_INTEGER));
    }
  }
  
  // Apply sorting
  if (sortConfig.key) {
    result.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle special cases for sorting
      if (sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
        aValue = new Date(aValue || 0).getTime();
        bValue = new Date(bValue || 0).getTime();
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
  
  return result;
};

// Helper function to read file as text
const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
};

export const usePaymentAdmin = (itemsPerPage = 25) => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formTypeFilter, setFormTypeFilter] = useState('all');
  const [amountRange, setAmountRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Transaction verification states
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState(null);
  const [transactionIdsToVerify, setTransactionIdsToVerify] = useState('');
  const [verificationMode, setVerificationMode] = useState('manual');
  const [bankStatementFile, setBankStatementFile] = useState(null);
  const [verificationStats, setVerificationStats] = useState({
    total: 0, matched: 0, unmatched: 0, alreadyVerified: 0
  });

  // Fetch payments data
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await paymentService.getAllPayments();
      setPayments(data);
      setFilteredPayments(data);
      setTotalPages(Math.ceil(data.length / itemsPerPage));
      setCurrentPage(1);
      setSelectedPayments([]);
      setSelectAll(false);
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  // Get paginated data
  const getPaginatedData = useCallback(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredPayments.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, filteredPayments, itemsPerPage]);

  // Initial data fetch
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Apply filters
  useEffect(() => {
    const result = filterPayments(payments, {
      statusFilter, searchTerm, formTypeFilter, amountRange, sortConfig
    });
    
    setFilteredPayments(result);
    setTotalPages(Math.ceil(result.length / itemsPerPage));
    setCurrentPage(1);
    setSelectedPayments([]);
    setSelectAll(false);
  }, [payments, statusFilter, searchTerm, formTypeFilter, amountRange, sortConfig, itemsPerPage]);

  // Pagination handler
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  }, [totalPages]);

  // Sorting handler
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Selection handlers
  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(getPaginatedData().map(payment => payment._id));
    }
    setSelectAll(!selectAll);
  }, [selectAll, getPaginatedData]);

  const handleSelectPayment = useCallback((id) => {
    setSelectedPayments(prev => 
      prev.includes(id) ? prev.filter(paymentId => paymentId !== id) : [...prev, id]
    );
  }, []);

  // Payment status update handlers
  const handleVerifyPayment = useCallback(async (id) => {
    const res = await Swal.fire({
      title: 'Verify Payment',
      text: 'Mark this payment as verified?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Verify',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#6B7280',
    });

    if (res.isConfirmed) {
      try {
        await paymentService.updatePaymentStatus(id, 'verified');
        await fetchPayments();
        Swal.fire({
          title: 'Verified!',
          text: 'Payment updated successfully.',
          icon: 'success',
          confirmButtonColor: '#10B981',
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Verification failed.',
          icon: 'error',
          confirmButtonColor: '#3b82f6',
        });
      }
    }
  }, [fetchPayments]);

  const handleRejectPayment = useCallback(async (id) => {
    const res = await Swal.fire({
      title: 'Reject Payment',
      text: 'Mark this payment as rejected?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Reject',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
    });

    if (res.isConfirmed) {
      try {
        await paymentService.updatePaymentStatus(id, 'rejected');
        await fetchPayments();
        Swal.fire({
          title: 'Rejected!',
          text: 'Payment updated successfully.',
          icon: 'success',
          confirmButtonColor: '#EF4444',
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Rejection failed.',
          icon: 'error',
          confirmButtonColor: '#3b82f6',
        });
      }
    }
  }, [fetchPayments]);

  const handleRevertStatus = useCallback(async (payment) => {
    const newStatus = payment.status === 'verified' ? 'rejected' : 'verified';
    const res = await Swal.fire({
      title: `Revert to ${newStatus}`,
      icon: 'question',
      text: `Change payment status to ${newStatus}?`,
      showCancelButton: true,
      confirmButtonText: `Set as ${newStatus}`,
      cancelButtonText: 'Cancel',
      confirmButtonColor: newStatus === 'verified' ? '#10B981' : '#EF4444',
      cancelButtonColor: '#6B7280',
    });

    if (res.isConfirmed) {
      try {
        await paymentService.updatePaymentStatus(payment._id, newStatus);
        await fetchPayments();
        Swal.fire({
          title: 'Updated!',
          text: `Payment marked as ${newStatus}.`,
          icon: 'success',
          confirmButtonColor: newStatus === 'verified' ? '#10B981' : '#EF4444',
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Status update failed.',
          icon: 'error',
          confirmButtonColor: '#3b82f6',
        });
      }
    }
  }, [fetchPayments]);

  // Bulk action handler
  const handleBulkAction = useCallback(async (action) => {
    if (selectedPayments.length === 0) {
      Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one payment',
        icon: 'info',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }

    const actionText = action === 'verify' ? 'verify' : 'reject';
    const res = await Swal.fire({
      title: `Bulk ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
      text: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} ${selectedPayments.length} selected payments?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: action === 'verify' ? 'Verify All' : 'Reject All',
      cancelButtonText: 'Cancel',
      confirmButtonColor: action === 'verify' ? '#10B981' : '#EF4444',
      cancelButtonColor: '#6B7280',
    });

    if (res.isConfirmed) {
      try {
        // Implement bulk update here
        for (const id of selectedPayments) {
          await paymentService.updatePaymentStatus(id, action === 'verify' ? 'verified' : 'rejected');
        }
        await fetchPayments();
        Swal.fire({
          title: 'Success!',
          text: `${selectedPayments.length} payments have been ${action === 'verify' ? 'verified' : 'rejected'}.`,
          icon: 'success',
          confirmButtonColor: action === 'verify' ? '#10B981' : '#EF4444',
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Bulk action failed.',
          icon: 'error',
          confirmButtonColor: '#3b82f6',
        });
      }
    }
  }, [selectedPayments, fetchPayments]);

  // Transaction verification handler
  const handleVerifyTransactionIds = useCallback(async () => {
    if (!transactionIdsToVerify.trim() && verificationMode === 'manual') {
      Swal.fire({
        title: 'Empty Input',
        text: 'Please enter transaction IDs to verify',
        icon: 'info',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }
    
    if (!bankStatementFile && verificationMode === 'file') {
      Swal.fire({
        title: 'No File Selected',
        text: 'Please select a bank statement file',
        icon: 'info',
        confirmButtonColor: '#3b82f6',
      });
      return;
    }
    
    setIsVerifying(true);
    
    try {
      let transactionIds = [];
      
      // Extract transaction IDs based on verification mode
      if (verificationMode === 'manual') {
        // Split by newlines, commas, or spaces and filter out empty strings
        transactionIds = transactionIdsToVerify
          .split(/[\n,\s]+/)
          .map(id => id.trim())
          .filter(id => id);
      } else if (verificationMode === 'file') {
        const fileContent = await readFileAsText(bankStatementFile);
        // Extract transaction IDs from file (implementation depends on file format)
        // This is a simplified example
        transactionIds = fileContent
          .split(/[\n,\s]+/)
          .map(id => id.trim())
          .filter(id => id);
      }
      
      if (transactionIds.length === 0) {
        setIsVerifying(false);
        Swal.fire({
          title: 'No Transaction IDs',
          text: 'No valid transaction IDs found',
          icon: 'warning',
          confirmButtonColor: '#3b82f6',
        });
        return;
      }
      
      // Process verification (match against payments)
      const matched = [];
      const unmatched = [];
      const alreadyVerified = [];
      
      for (const id of transactionIds) {
        const payment = payments.find(p => p.transactionId === id);
        
        if (payment) {
          if (payment.status === 'verified') {
            alreadyVerified.push({ transactionId: id, payment });
          } else {
            matched.push({ transactionId: id, payment });
          }
        } else {
          unmatched.push(id);
        }
      }
      
      // Store results
      const results = { matched, unmatched, alreadyVerified };
      setVerificationResults(results);
      setVerificationStats({
        total: transactionIds.length,
        matched: matched.length,
        unmatched: unmatched.length,
        alreadyVerified: alreadyVerified.length
      });
      
      // Show summary dialog
      Swal.fire({
        title: 'Verification Complete',
        html: `
          <div class="text-left">
            <p><strong>Total IDs:</strong> ${transactionIds.length}</p>
            <p><strong>Matched:</strong> ${results.matched.length}</p>
            <p><strong>Already Verified:</strong> ${results.alreadyVerified.length}</p>
            <p><strong>Unmatched:</strong> ${results.unmatched.length}</p>
          </div>
        `,
        icon: 'info',
        confirmButtonColor: '#3b82f6',
      });
      
    } catch (error) {
      Swal.fire({
        title: 'Verification Error',
        text: error.message || 'Failed to verify transaction IDs',
        icon: 'error',
        confirmButtonColor: '#3b82f6',
      });
    } finally {
      setIsVerifying(false);
    }
  }, [bankStatementFile, payments, transactionIdsToVerify, verificationMode]);

  // Handle verifying all matched transactions
  const handleVerifyAllMatched = useCallback(async () => {
    if (!verificationResults || verificationResults.matched.length === 0) {
      return;
    }

    const res = await Swal.fire({
      title: 'Verify All Matched Payments',
      text: `Are you sure you want to verify all ${verificationResults.matched.length} matched payments?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Verify All',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#6B7280',
    });

    if (res.isConfirmed) {
      try {
        const paymentIds = verificationResults.matched.map(item => item.payment._id);
        
        // Show processing indicator
        Swal.fire({
          title: 'Processing',
          text: 'Verifying payments...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        // Process each payment verification in sequence
        for (const id of paymentIds) {
          await paymentService.updatePaymentStatus(id, 'verified');
        }
        
        // Refresh data and reset verification state
        await fetchPayments();
        setVerificationResults(null);
        setTransactionIdsToVerify('');
        setBankStatementFile(null);
        
        Swal.fire({
          title: 'Success!',
          text: `${paymentIds.length} payments have been verified.`,
          icon: 'success',
          confirmButtonColor: '#10B981',
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Failed to verify payments.',
          icon: 'error',
          confirmButtonColor: '#3b82f6',
        });
      }
    }
  }, [fetchPayments, verificationResults]);

  // Handle file upload
  const handleFileChange = useCallback((e) => {
    if (e.target.files && e.target.files[0]) {
      setBankStatementFile(e.target.files[0]);
    }
  }, []);

  return {
    // State
    payments, filteredPayments, loading, statusFilter, searchTerm, 
    formTypeFilter, amountRange, currentPage, totalPages, isFilterExpanded, 
    sortConfig, selectedPayments, selectAll, isVerifying, verificationResults, 
    transactionIdsToVerify, verificationMode, bankStatementFile, verificationStats,
    
    // Actions
    setStatusFilter, setSearchTerm, setFormTypeFilter, setAmountRange,
    setIsFilterExpanded, setTransactionIdsToVerify, setVerificationMode,
    
    // Handlers
    fetchPayments, getPaginatedData, handlePageChange, handleSort,
    handleSelectAll, handleSelectPayment, handleVerifyPayment, handleRejectPayment,
    handleRevertStatus, handleBulkAction, handleVerifyTransactionIds,
    handleVerifyAllMatched, handleFileChange
  };
};